import { useNavigate } from "react-router-dom";
import { convertDecimalToDMS } from "../../earth/pixel";
import type { SoldPixel } from "../api";

const DEFAULT_IMG = "/static/img/gal12.jpg";

export function SoldPixelDetailModal({
  pixel,
  onClose,
  onDelete,
  onUndo,
}: {
  pixel: SoldPixel;
  onClose: () => void;
  onDelete?: () => void;
  onUndo?: () => void;
}) {
  const navigate = useNavigate();

  const imgSrc = pixel.image
    ? pixel.image.startsWith("/")
      ? pixel.image
      : `/gallery/${pixel.image}`
    : DEFAULT_IMG;

  const coords =
    pixel.info &&
    `${convertDecimalToDMS(pixel.info.lat, "lat")}, ${convertDecimalToDMS(
      pixel.info.lng,
      "lng",
    )}`;

  const frameColor = pixel.frameColor || pixel.color || "#000000";
  const available = pixel.ownership_status === "available_again";

  const openOnMap = () => {
    onClose();
    navigate("/world", { state: { searchQuery: String(pixel.pixel) } });
  };

  return (
    <div
      className="admin-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="admin-modal" role="dialog" aria-modal="true">
        <button
          type="button"
          className="admin-modal-close"
          aria-label="Close"
          onClick={onClose}
        >
          ×
        </button>

        <h2 className="admin-modal-title">Pixel {pixel.pixel}</h2>

        <div className="admin-modal-body">
          <div className="admin-modal-image-wrap">
            <img
              src={imgSrc}
              alt=""
              style={{
                width: "100%",
                maxHeight: 280,
                objectFit: "cover",
                border: `4px solid ${frameColor}`,
                borderRadius: 8,
                opacity: available ? 0.7 : 1,
              }}
            />
          </div>

          <div className="admin-modal-info">
            <p>
              <strong>Status:</strong>{" "}
              <span
                className={`admin-badge ${
                  available ? "admin-badge-available" : "admin-badge-live"
                }`}
              >
                {available ? "Available again" : "Owned"}
              </span>
            </p>
            <p>
              <strong>Artist:</strong> {pixel.artistName || "—"}
            </p>
            <p>
              <strong>Frame name:</strong> {pixel.frameName || "—"}
            </p>
            <p>
              <strong>Buyer:</strong> {pixel.user_name || "—"}
              {pixel.user_email ? ` (${pixel.user_email})` : ""}
            </p>
            <p>
              <strong>Location:</strong> {pixel.location || "—"}
            </p>
            <p>
              <strong>Country:</strong> {pixel.country || "—"}
            </p>
            <p>
              <strong>Region:</strong> {pixel.region_name || "—"}
            </p>
            <p>
              <strong>Coordinates:</strong> {coords || "—"}
            </p>
            <p>
              <strong>Sold at:</strong>{" "}
              {pixel.bought_at
                ? new Date(pixel.bought_at).toLocaleString()
                : "—"}
            </p>
            {available && pixel.deleted_at ? (
              <p>
                <strong>Deleted at:</strong>{" "}
                {new Date(pixel.deleted_at).toLocaleString()}
                {pixel.deleted_by_name ? ` by ${pixel.deleted_by_name}` : ""}
              </p>
            ) : null}
            {available && pixel.delete_comment ? (
              <p>
                <strong>Delete comment:</strong> {pixel.delete_comment}
              </p>
            ) : null}
            <p>
              <strong>Color:</strong>{" "}
              <span
                style={{
                  display: "inline-block",
                  width: 14,
                  height: 14,
                  borderRadius: 3,
                  background: frameColor,
                  verticalAlign: "middle",
                  marginRight: 6,
                }}
              />
              {frameColor}
            </p>
          </div>
        </div>

        <div className="admin-modal-actions">
          <button
            type="button"
            className="admin-btn admin-btn-ghost"
            onClick={onClose}
          >
            Close
          </button>
          {onDelete ? (
            <button
              type="button"
              className="admin-btn admin-btn-danger"
              onClick={onDelete}
            >
              Delete
            </button>
          ) : null}
          {onUndo ? (
            <button
              type="button"
              className="admin-btn admin-btn-primary"
              onClick={onUndo}
            >
              Undo delete
            </button>
          ) : null}
          <button
            type="button"
            className="admin-btn admin-btn-primary"
            onClick={openOnMap}
          >
            Show on map
          </button>
        </div>
      </div>
    </div>
  );
}
